// Flight paths (Phase 7). A straight line reads as a drone, so the path is a
// Catmull-Rom spline from a start point just outside the camera frustum,
// through waypoints inside it, to the perch, with layered wander added on
// top. Everything numeric comes from config.flight.
import * as THREE from 'three';
import { config } from '../config.js';

const rand = (min, max) => min + Math.random() * (max - min);
const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];

/** A point in camera space expressed as fractions of the frustum at `depth`. */
function frustumPoint(camera, xFrac, yFrac, depth) {
  const halfH = depth * Math.tan(THREE.MathUtils.degToRad(camera.fov / 2));
  const halfW = halfH * camera.aspect;
  return camera.localToWorld(new THREE.Vector3(xFrac * halfW, yFrac * halfH, -depth));
}

/** Where a world point sits in the frustum: { xFrac, yFrac, depth }. */
function frustumFractions(camera, worldPoint) {
  const local = camera.worldToLocal(worldPoint.clone());
  const depth = Math.max(0.05, -local.z);
  const halfH = depth * Math.tan(THREE.MathUtils.degToRad(camera.fov / 2));
  const halfW = halfH * camera.aspect;
  return { xFrac: local.x / halfW, yFrac: local.y / halfH, depth };
}

/**
 * Cheap organic noise: a sum of sines at incommensurate frequencies with
 * random phases. Not simplex, but smooth, bounded and dependency-free.
 */
function makeWander(octaves = 3) {
  const parts = Array.from({ length: octaves }, (_, i) => ({
    f: (1.3 + i * 0.9) * rand(0.8, 1.2),
    p: rand(0, Math.PI * 2),
    a: 1 / (i + 1),
  }));
  const norm = parts.reduce((s, q) => s + q.a, 0);
  return (t) => parts.reduce((s, q) => s + q.a * Math.sin(t * q.f * Math.PI * 2 + q.p), 0) / norm;
}

// Ease: gentle start, long deceleration into the perch.
const easeInOutSine = (t) => -(Math.cos(Math.PI * t) - 1) / 2;
const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

export function makeArrivalPath({ camera, perch }) {
  const c = config.flight;
  const target = new THREE.Vector3().fromArray(perch.position);
  const normal = new THREE.Vector3().fromArray(perch.normal).normalize();
  const at = frustumFractions(camera, target);
  const side = pick([-1, 1]);

  const start = frustumPoint(camera, side * c.startXFrac, rand(c.startYFracMin, c.startYFracMax), at.depth * rand(0.85, 1.15));
  const approach = target.clone().addScaledVector(normal, c.approachOffsetM);
  const waypoints = [];
  for (let i = 0; i < c.waypoints; i++) {
    const k = (i + 1) / (c.waypoints + 1); // 0..1 along the way
    const x = THREE.MathUtils.lerp(side * c.startXFrac, at.xFrac, k) + rand(-c.waypointJitterFrac, c.waypointJitterFrac);
    const y = THREE.MathUtils.lerp(rand(c.startYFracMin, c.startYFracMax), at.yFrac, k) + rand(c.waypointRiseFracMin, c.waypointRiseFracMax);
    waypoints.push(frustumPoint(camera, x, y, at.depth * rand(0.8, 1.2)));
  }
  const curve = new THREE.CatmullRomCurve3([start, ...waypoints, approach, target], false, 'centripetal', 0.5);
  const length = curve.getLength();
  const duration = THREE.MathUtils.clamp(length / c.speedMps, c.arriveMinS, c.arriveMaxS);
  const wander = [makeWander(), makeWander(), makeWander()];

  return {
    kind: 'arrival',
    duration,
    length,
    target,
    normal,
    /** Position and heading at normalised time t (0..1). */
    sample(t, out = {}) {
      const u = THREE.MathUtils.clamp(0.5 * easeInOutSine(t) + 0.5 * easeOutCubic(t), 0, 1);
      const pos = curve.getPointAt(u);
      const tangent = curve.getTangentAt(Math.min(u, 0.999));
      // Wander fades out over the last stretch so the landing is exact.
      const fade = THREE.MathUtils.smoothstep(1 - u, 0, c.wanderFadeFrac);
      const amp = c.wanderAmpM * fade;
      const wt = t * duration;
      pos.x += amp * wander[0](wt);
      pos.y += amp * wander[1](wt) * c.wanderVerticalScale;
      pos.z += amp * wander[2](wt);
      out.position = pos;
      out.tangent = tangent;
      out.u = u;
      out.wander = fade;
      return out;
    },
  };
}

export function makeExitPath({ camera, from }) {
  const c = config.flight;
  const at = frustumFractions(camera, from);
  const side = pick([-1, 1]);
  const lift = from.clone().add(new THREE.Vector3(0, c.exitLiftM, 0));
  const mid = frustumPoint(camera, THREE.MathUtils.lerp(at.xFrac, side * 0.7, 0.6), at.yFrac + 0.5, at.depth * 0.9);
  const end = frustumPoint(camera, side * c.exitXFrac, c.exitYFrac, at.depth * 0.7);
  const curve = new THREE.CatmullRomCurve3([from.clone(), lift, mid, end], false, 'centripetal', 0.5);
  const wander = [makeWander(), makeWander(), makeWander()];
  const duration = c.exitS;
  return {
    kind: 'exit',
    duration,
    length: curve.getLength(),
    sample(t, out = {}) {
      const u = THREE.MathUtils.clamp(t * t * (3 - 2 * t) * 0.3 + t * 0.7, 0, 1); // slightly eased start, then linear
      const pos = curve.getPointAt(u);
      const tangent = curve.getTangentAt(Math.min(u, 0.999));
      const amp = c.wanderAmpM * THREE.MathUtils.smoothstep(u, 0, 0.2);
      const wt = t * duration;
      pos.x += amp * wander[0](wt);
      pos.y += amp * wander[1](wt) * c.wanderVerticalScale;
      pos.z += amp * wander[2](wt);
      out.position = pos;
      out.tangent = tangent;
      out.u = u;
      return out;
    },
  };
}
